import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { useMutation } from "@tanstack/react-query";
import { useScheduleContext } from "../hooks/context/useScheduleContext";
import { useFilters } from "../../../../utils/hooks/useFilters";
import useApi from "../../../../utils/api";
import { formatTimeSheetParams } from "../../../TimeSheet/api/helpers";
import { formatEvents } from "../helpers/data/formatEvents";
import { getErrorMessage } from "../../../../utils/helpers/errors";
import { withLoadingToast } from "../helpers/withLoadingToast";

const ACTION_LOADING_KEYS: Record<string, string> = {
    submit: "submitting",
    publish: "publishing",
    unpublish: "unpublishing",
    lock: "locking",
    unlock: "unlocking",
};

export const useActionsMutations = ({ values, close }: { values: any; close: () => void }) => {
    const { t } = useTranslation();
    const { authPut, authGet } = useApi();
    const [filters] = useFilters();
    const { type, dispatch, resetCount, clearSelectedEvents, refetchEventStatistics } = useScheduleContext();
    const isTeamScheduler = type === "team";

    const data: any = useMemo(() => {
        let from = filters?.from;
        let to = filters?.to;
        if (filters?.mode === "month") {
            from = moment(filters?.from).startOf("week").format("YYYY-MM-DD");
            to = moment(filters?.to).endOf("week").format("YYYY-MM-DD");
        }

        if (values?.type === "bulk-day") {
            from = moment(values?.day).format("YYYY-MM-DD");
            to = moment(values?.day).format("YYYY-MM-DD");
        }
        const params: any = {
            from,
            to,
            filters: {
                pageIndex: Number(filters?.page) - 1,
                pageSize: filters?.pageSize,
                ...formatTimeSheetParams({
                    filters: filters?.filters,
                    from,
                    to
                }),
                sort: filters?.sort
            }
        };

        if (values?.type === "bulk-day" || (values?.type === "bulk" && filters?.mode === "month")) {
            delete params.filters.pageIndex;
            delete params.filters.pageSize;
        }

        if (values?.type === "bulk" || values?.type === "day" || (values?.type === "bulk-day" && values?.ids)) {
            params.ids = values?.ids || [];
        }
        return params;
    }, [filters, values?.type, values?.ids, values?.day]);

    const onSuccess = useCallback(async () => {
        if (values?.userId) {
            const endpoint = isTeamScheduler ? `/schedules/v2/${values?.userId}/events` : "/my/schedules/v2/events";
            const response = await authGet(endpoint, {
                params: data?.filters
            });

            if (response?.length) {
                const events = response?.flat();
                if (filters?.mode === "month") {
                    const type = values?.moreModal
                        ? "update-more-modal-events"
                        : "update-calendar-events";
                    dispatch({ type, payload: formatEvents(events) });
                } else {
                    dispatch({ type: "update-events", payload: formatEvents(events) });
                }
            }
        } else {
            dispatch({ type: "refetch-events" });
        }
        clearSelectedEvents();
        resetCount?.();
        refetchEventStatistics();
        close();
    }, [
        values?.userId,
        values?.moreModal,
        clearSelectedEvents,
        resetCount,
        refetchEventStatistics,
        close,
        isTeamScheduler,
        authGet,
        data?.filters,
        filters?.mode,
        dispatch
    ]);

    const loadingToast = withLoadingToast(
        t(ACTION_LOADING_KEYS[values?.action] ?? "processing"),
        "schedule-action"
    );

    const submitMutation = useMutation({
        ...loadingToast,
        mutationFn: () => {
            const endpoint = isTeamScheduler
                ? `/schedules/v2/${values?.action}/${values?.eventId}`
                : `/my/schedules/v2/${values?.action}/${values?.eventId}`;
            return authPut(endpoint);
        },
        onSuccess,
        onError: (err) => getErrorMessage(err, t),
    });

    const submitUserMutation = useMutation({
        ...loadingToast,
        mutationFn: () => {
            const endpoint = isTeamScheduler
                ? `/schedules/v2/${values?.action}/user/${values?.userId}`
                : `/my/schedules/v2/${values?.action}/user`;
            return authPut(endpoint, { data });
        },
        onSuccess,
        onError: (err) => getErrorMessage(err, t),
    });

    const bulkSubmitMutation = useMutation({
        ...loadingToast,
        mutationFn: () => {
            const endpoint = isTeamScheduler
                ? `/schedules/v2/${values?.action}/bulk`
                : `/my/schedules/v2/${values?.action}/bulk`;
            return authPut(endpoint, { data });
        },
        onSuccess,
        onError: (err) => getErrorMessage(err, t),
    });

    return { submitMutation, submitUserMutation, bulkSubmitMutation };
};
